import { Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { PlaceholderColorPicker } from "../../controls/PlaceholderColorPicker";
import { useMemo } from "react";
import { ItemControlsConfigProvider } from "../../controls/ItemControlsConfigProvider";
import { PanelSection } from "../PanelSection";
import { DEFAULT_STROKE_COLOR } from "../../../constants";
import { ItemAlignmentFeature } from "../../features/ItemAlignmentFeature";
import { getUpdatedItemPositionOnAddingStroke } from "../../../helpers/tabHelpers";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { LINE_ORIENTATIONS, Shape, SHAPE_TYPES } from "../../../types";
import { StrokeColorControl } from "../../controls/StrokeColorControl";
import { Stroke } from "../../controls/Stroke";
import { BorderRadius } from "../../controls/BorderRadius";
import { getRelativeDimentionsToCalculateRadius } from "../../../helpers";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { ToolbarDivider } from "../../common/ToolbarDivider";

export const ShapeControls = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const {
    brandKitData: { brandKitPalette: palette },
  } = useTemplateData();
  const t = useTranslate();
  const { currentItem, shapes, slideLayoutShapes, updateShape } =
    useTemplateEditorStore((state) => ({
      currentItem: state.templateState.currentItem,
      shapes: state.templateState.shapes,
      slideLayoutShapes: state.templateState.slideLayoutShapes,
      updateShape: state.updateShape,
    }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const currentShape =
    shapes.find((image) => image.id === currentItem?.id) ??
    slideLayoutShapes?.find((shape) => shape.id === currentItem?.id);
  const isCurrentShapeLine = currentShape?.shapeType === SHAPE_TYPES.LINE;
  const isHorizontalLine =
    isCurrentShapeLine &&
    currentShape.orientation === LINE_ORIENTATIONS.HORIZONTAL;

  const lineThickness = useMemo(() => {
    if (!isCurrentShapeLine) return;
    if (currentShape.orientation === LINE_ORIENTATIONS.HORIZONTAL)
      return Math.round(currentShape.size.height * previewHeight);
    return Math.round(currentShape.size.width * previewWidth);
  }, [currentShape, isCurrentShapeLine, previewHeight, previewWidth]);

  const relativeDimentionsToCalculateRadius =
    getRelativeDimentionsToCalculateRadius(previewWidth, previewHeight);

  const showRadius = useMemo(() => {
    return currentShape?.shapeType === SHAPE_TYPES.RECTANGLE;
  }, [currentShape]);

  if (!currentShape) return null;

  const strokeControl = (
    <Stroke
      value={
        isCurrentShapeLine
          ? lineThickness
          : Math.round((currentShape.stroke?.width ?? 0) * previewWidth)
      }
      max={isCurrentShapeLine ? 15 : undefined}
      onChange={(value) => {
        if (isCurrentShapeLine) {
          updateShape({
            ...currentShape,
            size: {
              width: isHorizontalLine
                ? currentShape.size.width
                : (value ?? 0) / previewWidth,
              height: isHorizontalLine
                ? (value ?? 0) / previewHeight
                : currentShape.size.height,
            },
          });
        } else {
          const { x, y } = getUpdatedItemPositionOnAddingStroke(
            value ?? 0,
            currentShape.position,
            currentShape.size,
            previewWidth,
            previewHeight
          );
          updateShape({
            ...currentShape,
            stroke: value
              ? {
                  ...currentShape.stroke,
                  width: value / previewWidth,
                  color: currentShape.stroke?.color ?? DEFAULT_STROKE_COLOR,
                }
              : {
                  ...currentShape.stroke,
                  width: null,
                  color: null,
                },
            position: {
              x,
              y,
            },
          });
        }
        addTemplateUpdate();
      }}
    />
  );

  const strokeColorControl = !isCurrentShapeLine && (
    <StrokeColorControl
      item={currentShape}
      pickedItemAction={updateShape}
      previewWidth={previewWidth}
      palette={palette}
    />
  );

  const borderRadiusControl = showRadius && (
    <BorderRadius
      value={currentShape.stroke?.radius ?? 0}
      maxValue={Math.round((currentShape.size.width / 2) * previewWidth)}
      onChange={(value) => {
        updateShape({
          ...currentShape,
          stroke: { ...currentShape.stroke, radius: value },
        });
        addTemplateUpdate();
      }}
      relativeDimentions={relativeDimentionsToCalculateRadius}
    />
  );

  const backgroundControl = (
    <PlaceholderColorPicker
      key={currentShape.id}
      shape="round"
      palette={palette}
      value={currentShape.background}
      allowClear={true}
      onClear={() => {
        updateShape({
          ...currentShape,
          background: undefined,
        });
        addTemplateUpdate();
      }}
      onChangeComplete={(resolvedColor) => {
        updateShape({
          ...currentShape,
          background: resolvedColor,
        });
        addTemplateUpdate();
      }}
      title={t("components.templates.settings.background")}
      mode={isCurrentShapeLine ? "single" : ["single", "gradient"]}
    />
  );

  const alignmentControl = (popover: boolean) => (
    <ItemAlignmentFeature
      pickedItem={currentShape}
      onChange={(value) => {
        updateShape(value as Partial<Shape>);
        addTemplateUpdate();
      }}
      previewWidth={previewWidth}
      previewHeight={previewHeight}
      popover={popover}
    />
  );

  return (
    <ItemControlsConfigProvider>
      <PanelSection title={t("components.templates.settings.shapeStyle")}>
        <Flex gap={16} align="start">
          {alignmentControl(false)}
          <ToolbarDivider style={{ alignSelf: "stretch", height: "auto" }} />
          <Flex vertical gap={12}>
            <Flex align="center" gap={8}>
              {backgroundControl}
            </Flex>
            <Flex align="center" gap={8} wrap="wrap">
              {strokeControl}
              {strokeColorControl}
              {borderRadiusControl}
            </Flex>
          </Flex>
        </Flex>
      </PanelSection>
    </ItemControlsConfigProvider>
  );
};
